import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { UserList } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionResponsibleBucket } from '../types/statistics.types';
import { MetricInfo } from './MetricInfo';

interface Props {
  data?: AcquisitionResponsibleBucket[];
  loading: boolean;
}

interface RowMeta extends AcquisitionResponsibleBucket {
  conversion: number;
}

export const ResponsiblePerformanceTable = ({ data, loading }: Props) => {
  const rows: RowMeta[] = useMemo(() => {
    if (!data) return [];
    return [...data]
      .map((d) => {
        const decided = d.deals + d.rejected;
        const conversion = decided > 0 ? d.deals / decided : 0;
        return { ...d, conversion };
      })
      .sort((a, b) => b.deals - a.deals || b.totalVolume - a.totalVolume);
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <UserList className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-48" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[260px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <UserList className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Performance per teamlid</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Per verantwoordelijke: aantal dossiers, gewonnen deals,
                  conversie, transactievolume en gemiddelde yield.
                </p>
                <p>
                  <strong>Conversie:</strong> deals / (deals + afgewezen).
                </p>
                <p>
                  <strong>Volume:</strong> Σ <code>(totalBid || bidPriceActual ||
                  grossAskingPrice)</code> over alle dossiers van die persoon.
                </p>
                <p>
                  <strong>Gem. yield:</strong> rekenkundig gemiddelde van{' '}
                  <code>bidPriceActualNetYield</code> over dossiers met ingevulde
                  yield.
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Hoeveel dossiers, deals, conversie, volume en yield per verantwoordelijke
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex h-[120px] items-center justify-center text-sm text-neutral-secondary-mid">
            Geen data beschikbaar
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-neutral-100">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50/70 text-xs uppercase tracking-wide text-neutral-secondary-mid">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Teamlid</th>
                  <th className="px-3 py-2 text-right font-medium">Dossiers</th>
                  <th className="px-3 py-2 text-right font-medium">Deals</th>
                  <th className="px-3 py-2 text-right font-medium">Conversie</th>
                  <th className="px-3 py-2 text-right font-medium">Volume</th>
                  <th className="px-3 py-2 text-right font-medium">Gem. yield</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map((r) => (
                  <tr key={r.userId} className="hover:bg-neutral-50/50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          {r.avatarUrl && (
                            <AvatarImage src={r.avatarUrl} alt={r.displayName} />
                          )}
                          <AvatarFallback className="text-[10px]">
                            {r.displayName
                              .split(' ')
                              .map((p) => p[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-neutral-primary">
                          {r.displayName}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.count}</td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <Badge
                        variant="secondary"
                        className="bg-moss-primary/10 text-moss-primary"
                      >
                        {r.deals}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.deals + r.rejected > 0
                        ? `${(r.conversion * 100).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {formatCurrency(r.totalVolume)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.avgYield > 0 ? `${(r.avgYield * 100).toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
